import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut,
  updateProfile,
  onAuthStateChanged,
  deleteUser
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp, FieldValue } from 'firebase/firestore';
import { auth, db } from '../firebase';
import config from '../config';
import { TRIAL_MS } from '../utils/accessControl';

export interface UserData {
  uid: string;
  email: string;
  eczaneAdi: string;
  glnNumarasi: string;
  il: string;
  ilce: string;
  adres: string;
  telefon: string;
  name?: string; // Ad Soyad
  isAdmin: boolean;
  isActive?: boolean;
  isApproved: boolean;
  trialStartDate?: Date; // Ücretsiz deneme başlangıç tarihi
  trialEndDate?: Date;   // Ücretsiz deneme bitiş tarihi
  hasActiveSubscription?: boolean; // Aktif abonelik durumu
  createdAt: Date;
  updatedAt?: Date;
}

// Login fonksiyonu
export const login = async (email: string, password: string) => {
  try {
    console.log('🔄 Giriş işlemi başlatılıyor:', email);
    
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    console.log('✅ Giriş başarılı:', user.uid);
    return user;
    
  } catch (error: any) {
    console.error('❌ Giriş hatası:', error);
    console.error('❌ Hata kodu:', error.code);
    console.error('❌ Hata mesajı:', error.message);
    
    let userMessage = 'Giriş başarısız.';
    
    switch (error.code) {
      case 'auth/user-not-found':
        userMessage = 'Kullanıcı bulunamadı. Lütfen e-posta adresinizi kontrol edin.';
        break;
      case 'auth/wrong-password':
        userMessage = 'Yanlış şifre. Lütfen şifrenizi kontrol edin.';
        break;
      case 'auth/invalid-credential':
        userMessage = 'E-posta veya şifre hatalı. Lütfen bilgilerinizi kontrol edin.';
        break;
      case 'auth/invalid-email':
        userMessage = 'Geçersiz e-posta adresi.';
        break;
      case 'auth/too-many-requests':
        userMessage = 'Çok fazla başarısız giriş denemesi. Lütfen bir süre sonra tekrar deneyin.';
        break;
      default:
        userMessage = error.message || 'Bilinmeyen bir hata oluştu.';
    }
    
    throw new Error(userMessage);
  }
};

// Register fonksiyonu
export const register = async (email: string, password: string, userData: Omit<UserData, 'uid' | 'email' | 'createdAt'>) => {
  try {
    console.log(' Kayıt işlemi başlıyor:', email);
    console.log('🔍 GLN Numarası:', userData.glnNumarasi);

    // 1. Önce backend'de GLN kontrolü yap
    console.log('🔍 Backend GLN kontrolü yapılıyor...');
    const glnCheckResponse = await fetch(`${config.API_BASE_URL}/api/auth/check-gln`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ glnNumarasi: userData.glnNumarasi })
    });

    console.log('📡 GLN Kontrol Response Status:', glnCheckResponse.status);
    const glnCheckResult = await glnCheckResponse.json();
    console.log(' GLN Kontrol Sonucu:', glnCheckResult);

    if (glnCheckResult.exists) {
      console.log('❌ GLN zaten kayıtlı!');
      throw new Error(`Bu GLN numarası zaten kayıtlı! Eczane: ${glnCheckResult.user.eczaneAdi} (${glnCheckResult.user.email})`);
    }

    console.log('✅ GLN kullanılabilir, kayıt devam ediyor...');

    // 2. Firebase Auth ile kullanıcı oluştur
    console.log(' Firebase Auth kullanıcısı oluşturuluyor...');
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    console.log('✅ Firebase Auth kullanıcısı oluşturuldu:', user.uid);

    // 3. Kullanıcı profilini güncelle
    await updateProfile(user, {
      displayName: userData.eczaneAdi
    });
    console.log('✅ Kullanıcı profili güncellendi');

    // 4. Auth state garantisi için yeniden giriş yap
    console.log('⏳ Auth state garantisi için yeniden giriş yapılıyor...');
    await signInWithEmailAndPassword(auth, email, password);
    console.log('✅ Yeniden giriş başarılı - auth state garantili');

    // 5. Firestore'a kullanıcı bilgilerini kaydet
    // 30 günlük ücretsiz deneme başlat
    const now = new Date();
    const trialEndDate = new Date(now.getTime() + TRIAL_MS);
    
    const userDocData = {
      uid: user.uid,
      email: email,
      ...userData,
      isActive: (userData as any).isActive !== undefined ? (userData as any).isActive : true, // Varsayılan olarak aktif
      isApproved: (userData as any).isApproved !== undefined ? (userData as any).isApproved : true, // Varsayılan olarak onaylı
      trialStartDate: now,
      trialEndDate: trialEndDate,
      hasActiveSubscription: false,
      createdAt: now,
      updatedAt: now
    };

    console.log('💾 Firestore\'a kaydedilecek veri:', userDocData);
    console.log(' Belge yolu: /users/' + user.uid);

    await setDoc(doc(db, 'users', user.uid), userDocData);
    console.log('✅ Firestore\'a kayıt başarılı - /users/' + user.uid);

    return user;
    
  } catch (error: any) {
    console.error('❌ Kayıt hatası:', error);
    console.error('❌ Kayıt hatası detayı:', error);
    console.error('❌ Hata kodu:', error.code);
    console.error('❌ Hata mesajı:', error.message);
    
    let userMessage = 'Kayıt işlemi başarısız oldu.';
    
    // GLN kontrol hatası
    if (error.message.includes('GLN numarası zaten kayıtlı')) {
      userMessage = error.message;
    } else {
      // Firebase hatalarını kontrol et
      switch (error.code) {
        case 'auth/email-already-in-use':
          userMessage = 'Bu e-posta adresi zaten kullanımda. Lütfen farklı bir e-posta adresi kullanın.';
          break;
        case 'auth/invalid-email':
          userMessage = 'Geçersiz e-posta adresi.';
          break;
        case 'auth/weak-password':
          userMessage = 'Şifre çok zayıf. En az 6 karakter kullanın.';
          break;
        default:
          userMessage = error.message || 'Bilinmeyen bir hata oluştu.';
      }
    }
    
    throw new Error(userMessage);
  }
};

// Logout fonksiyonu
export const logout = async () => {
  try {
    await signOut(auth);
    console.log('✅ Çıkış başarılı');
  } catch (error) {
    console.error('❌ Çıkış hatası:', error);
    throw error;
  }
};

// Kullanıcı verilerini getir
export const getUserData = async (uid: string): Promise<UserData | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    
    if (userDoc.exists()) {
      return userDoc.data() as UserData;
    } else {
      console.log('❌ Kullanıcı verisi bulunamadı:', uid);
      return null;
    }
  } catch (error) {
    console.error('❌ Kullanıcı verisi alma hatası:', error);
    return null;
  }
};

// Auth state listener
export const onAuthStateChange = (callback: (user: any) => void) => {
  return onAuthStateChanged(auth, callback);
};
